import React, {
  useState, useCallback, useRef, useEffect, useMemo, 
} from 'react';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { 
  Button, 
  Dropdown,
} from 'react-bootstrap';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'react-bootstrap-icons';

import './CustomDataTable.scss';

// Компонент для фиксированной таблицы
  interface CustomDataTableProps {
    loading?: boolean
    columns?: ({
      name: string
      title: string
      width?: number
      minWidth?: number
      maxWidth?: number
      fixed?: boolean
      getCellValue?(...args: unknown[]): unknown
      sortValueConverter?(...args: unknown[]): unknown
      showSum?: boolean
      textAlign?: 'left' | 'right' | 'center'
    })[]
    data?: unknown[]
    dataLength?: number
    error?: { message?: string }
    errorRetryHandler?(...args: unknown[]): unknown
    sortField?: string
    sortOrder?: string
    noDataText?: string
    rowClassName?: string | ((...args: unknown[]) => unknown)
    hidePagination?: boolean
    localSorting?: boolean
    localPaging?: boolean
    currentPage?: number
    pageSize?: number
    onCurrentPageChange?(...args: unknown[]): unknown
    onPageSizeChange?(...args: unknown[]): unknown
    onSortingChange?(...args: unknown[]): unknown
    onRowClick?(...args: unknown[]): unknown
    keyboardScrollEnabled?: boolean
    // Новый проп для контроля включения строк в сумму
    shouldIncludeRowInSum?(...args: unknown[]): unknown
    // Новый проп: рисовать столбцы по указанной ширине
    useColumnWidth?: boolean
  }

const CustomDataTable = (props: CustomDataTableProps) => {
  const {
    loading = false,
    columns = [],
    data = [],
    dataLength = 0,
    error = null,
    errorRetryHandler = null,
    sortField = null,
    sortOrder = null,
    noDataText = 'По Вашему запросу ничего не найдено',
    rowClassName = null,
    hidePagination = false,
    localSorting = true,
    localPaging = true,
    currentPage: initialCurrentPage = 0,
    pageSize: initialPageSize = 10,
    onCurrentPageChange = null,
    onPageSizeChange = null,
    onSortingChange = null,
    onRowClick = null,
    keyboardScrollEnabled = true,
    // Новый проп для исключения строк из суммы
    shouldIncludeRowInSum = null,
    // Новый проп: рисовать столбцы по указанной ширине
    useColumnWidth = false,
  } = props ?? {};
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sorting, setSorting] = useState({
    columnName: sortField,
    direction: sortOrder?.toLowerCase() || 'asc',
  });
  
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const scrollContainerRef = useRef(null);
  const headerContainerRef = useRef(null);
  const tableContainerRef = useRef(null);

  const [isLoadingVisible, setIsLoadingVisible] = useState(false);

  // Эффект для плавного появления/исчезновения лоадера
  useEffect(() => {
    if (loading) {
      setIsLoadingVisible(true);
    } else {
    // Небольшая задержка перед скрытием, чтобы отыграла анимация fade-out
      const timer = setTimeout(() => setIsLoadingVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Защита от некорректных данных
  const safeColumns = useMemo(() => Array.isArray(columns) ? columns : [], [columns]);
  const safeData = useMemo(() => Array.isArray(data) ? data : [], [data]);

  // Проверка наличия столбцов с showSum
  const hasSumRow = useMemo(() => 
    safeColumns.some(col => col?.showSum), 
  [safeColumns]
  );

  // Расчет сумм по столбцам
  const sumRow = useMemo(() => {
    if (!hasSumRow || safeData.length === 0) return {};
    
    const sums = {};
    safeColumns.forEach(col => {
      if (col?.showSum && col?.name) {
        const total = safeData.reduce<number>((acc, row) => {
          if (!row) return acc;
          
          // Проверяем, нужно ли включать эту строку в сумму
          if (shouldIncludeRowInSum && typeof shouldIncludeRowInSum === 'function') {
            if (!shouldIncludeRowInSum(row, col.name)) {
              return acc;
            }
          }
          
          // Используем ИСХОДНОЕ значение из данных, а не результат getCellValue
          let value = row[col.name];
          
          // Применяем sortValueConverter если доступен
          if (col.sortValueConverter && typeof col.sortValueConverter === 'function') {
            value = col.sortValueConverter(value);
          }
          
          // Преобразуем в число
          let num;
          if (typeof value === 'number') {
            num = value;
          } else if (typeof value === 'string') {
            const cleaned = value.replace(/[^\d.,-]/g, '').replace(',', '.');
            num = parseFloat(cleaned);
          } else {
            num = parseFloat(value);
          }
          
          return acc + (isNaN(num) ? 0 : num);
        }, 0);
        
        sums[col.name] = Math.round(total);
      }
    });
    return sums;
  }, [safeData, safeColumns, hasSumRow, shouldIncludeRowInSum]);

  // Вычисляем ширину
  const totalWidth = useMemo(() => 
    safeColumns.reduce((sum, col) => sum + (col?.width || 200), 0), 
  [safeColumns]
  );
  
  // Синхронизация горизонтального скролла заголовка и тела таблицы
  useEffect(() => {
    const headerContainer = headerContainerRef.current;
    const bodyContainer = scrollContainerRef.current;
    
    if (!headerContainer || !bodyContainer) return;
    
    const handleScroll = () => {
      headerContainer.scrollLeft = bodyContainer.scrollLeft;
    };
    
    bodyContainer.addEventListener('scroll', handleScroll);
    return () => bodyContainer.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Обработка клавиатуры для скролла
  useEffect(() => {
    if (!keyboardScrollEnabled) return;

    const handleKeyDown = (e) => {
      const isFocusedOnTable = 
        document.activeElement === tableContainerRef.current ||
        document.activeElement === scrollContainerRef.current ||
        tableContainerRef.current?.contains(document.activeElement);

      if (!isFocusedOnTable) return;
      const container = scrollContainerRef.current;
      if (!container) return;

      const scrollStep = 50;
      const scrollStepLarge = 200;

      switch(e.key) {
        case 'ArrowLeft': e.preventDefault();container.scrollLeft -= scrollStep;break;
        case 'ArrowRight': e.preventDefault();container.scrollLeft += scrollStep;break;
        case 'ArrowUp': e.preventDefault();container.scrollTop -= scrollStep;break;
        case 'ArrowDown': e.preventDefault();container.scrollTop += scrollStep;break;
        case 'Home': 
          e.preventDefault();
          container.scrollLeft = e.ctrlKey ? 0 : container.scrollLeft;
          if (e.ctrlKey) container.scrollTop = 0;
          break;
        case 'End':
          e.preventDefault();
          container.scrollLeft = e.ctrlKey ? container.scrollWidth : container.scrollWidth;
          if (e.ctrlKey) container.scrollTop = container.scrollHeight - container.clientHeight;
          break;
        case 'PageUp': e.preventDefault();container.scrollTop -= scrollStepLarge;break;
        case 'PageDown': e.preventDefault();container.scrollTop += scrollStepLarge;break;
        default: break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    if (tableContainerRef.current) {
      tableContainerRef.current.tabIndex = 0;
      tableContainerRef.current.style.outline = 'none';
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [keyboardScrollEnabled]);

  // Обработчик сортировки
  const handleSort = useCallback((columnName) => {
    if (!columnName) return;
    const newDirection = sorting.columnName === columnName && sorting.direction === 'asc' ? 'desc' : 'asc';
    const newSorting = { columnName, direction: newDirection };
    setSorting(newSorting);
    if (onSortingChange) onSortingChange(columnName, newDirection.toUpperCase());
  }, [sorting, onSortingChange]);

  // Сортировка данных (использует ИСХОДНЫЕ данные из row[column.name], а не результат getCellValue)
  const getSortedData = useCallback(() => {
    if (!localSorting || !sorting.columnName || safeData.length === 0) return safeData;
    
    return [...safeData].sort((a, b) => {
      if (!a || !b) return 0;
      const column = safeColumns.find(col => col?.name === sorting.columnName);
      // Используем ИСХОДНОЕ значение из данных, а не результат getCellValue
      let valueA = a[sorting.columnName];
      let valueB = b[sorting.columnName];
      
      // Применяем sortValueConverter только к исходным данным
      if (column?.sortValueConverter && typeof column.sortValueConverter === 'function') {
        valueA = column.sortValueConverter(valueA);
        valueB = column.sortValueConverter(valueB);
      }
      
      if (valueA === valueB) return 0;
      const comparison = valueA < valueB ? -1 : 1;
      return sorting.direction === 'asc' ? comparison : -comparison;
    });
  }, [safeData, sorting, safeColumns, localSorting]);

  // Пагинация данных
  const getPaginatedData = useCallback(() => {
    const sortedData = getSortedData();
    if (!localPaging || hidePagination || sortedData.length === 0) return sortedData;
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [getSortedData, localPaging, hidePagination, currentPage, pageSize]);

  // Обработчики пагинации
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (onCurrentPageChange) onCurrentPageChange(newPage * pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    if (onPageSizeChange) onPageSizeChange(newSize);
    if (onCurrentPageChange) onCurrentPageChange(0);
  };

  // Рассчитываем общее количество страниц
  const totalPages = Math.ceil(dataLength / pageSize);

  // Получаем данные для отображения
  const displayData = useMemo(() => getPaginatedData(), [getPaginatedData]);

  // Вычисляем высоту на основе количества строк (с учетом строки сумм)
  const rowHeight = 40;
  const headerHeight = 60;
  const scrollbarOffset = 35;
  const minTableHeight = 220;
  const maxTableHeight = 700;

  const visibleRowsCount = displayData.length + (hasSumRow && displayData.length > 0 ? 1 : 0);
  const calculatedBodyHeight = visibleRowsCount * rowHeight + scrollbarOffset;
  const tableBodyHeight = Math.min(calculatedBodyHeight, maxTableHeight - headerHeight);
  const totalTableHeight = headerHeight + tableBodyHeight;

  if (isEmpty(safeData) && error) {
    return (
      <div className='text-center text-danger p-4'>
        <div>Ошибка загрузки данных</div>
        { error.message && <div className='small'>{ error.message }</div> }
        { errorRetryHandler && (
          <Button 
            variant='outline-danger' 
            size='sm' 
            className='mt-2'
            onClick={ errorRetryHandler }
          >
            Повторить
          </Button>
        ) }
      </div>
    );
  }

  // Компонент заголовка
  const renderHeaderCell = useCallback((column, index) => {
    if (!column || !column.name) return null;
    const isSorted = sorting.columnName === column.name;
    let left = 0;
    if (column.fixed) {
      for (let i = 0;i < index;i++) {
        if (safeColumns[i]?.fixed) {
          left += safeColumns[i].width || 200;
        }
      }
    }
  
    const textAlign = column.textAlign || 'right';
  
    return (
      <th
        key={ column.name }
        style={ {
          width: column.width || 'auto',
          minWidth: column.minWidth || column.width || 150,
          maxWidth: column.maxWidth || 'none',
          position: column.fixed ? 'sticky' : 'relative',
          left: column.fixed ? left : 'auto',
          zIndex: column.fixed ? 30 : 10,
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
          userSelect: 'none',
          boxShadow: column.fixed ? '2px 0 5px rgba(0,0,0,0.1)' : 'none',
          textAlign,
        } }
        onClick={ () => handleSort(column.name) }
        className={ classNames(
          'p-2',
          'border-bottom',
          'border-end',
          { 'bg-light': isSorted }
        ) }
      >
        <div className='d-flex align-items-center justify-content-between'>
          <span className='font-weight-bold'>{ column.title }</span>
          <span className='ml-2'>
            { isSorted ? (
              sorting.direction === 'asc' ? (
                <ChevronUp size={ 14 } />
              ) : (
                <ChevronDown size={ 14 } />
              )
            ) : (
              <span style={ { opacity: 0.3 } }>
                <ChevronUp size={ 14 } />
              </span>
            ) }
          </span>
        </div>
      </th>
    );
  }, [safeColumns, sorting, handleSort]);

  // Компонент ячейки с поддержкой любого типа возвращаемого значения из getCellValue
  // Пример для renderCell (остальные — аналогично)
  const renderCell = useCallback((row, column, rowIndex, columnIndex) => {
    if (!column || !column.name || !row) return null;
  
    let cellContent;
    if (typeof column.getCellValue === 'function') {
      cellContent = column.getCellValue(row, column.name);
    } else {
      const rawValue = row[column.name];
      cellContent = rawValue != null ? String(rawValue) : '';
    }
  
    let left = 0;
    if (column.fixed) {
      for (let i = 0;i < columnIndex;i++) {
        if (safeColumns[i]?.fixed) {
          left += safeColumns[i].width || 200;
        }
      }
    }
  
    const textAlign = column.textAlign || 'right';
  
    return (
      <td
        key={ `${ rowIndex }-${ column.name }` }
        style={ {
          width: column.width || 'auto',
          minWidth: column.minWidth || column.width || 150,
          maxWidth: column.maxWidth || 'none',
          position: column.fixed ? 'sticky' : 'relative',
          left: column.fixed ? left : `${ left }px`,
          zIndex: column.fixed ? 25 : 5,
          backgroundColor: 'white',
          verticalAlign: 'middle',
          boxShadow: column.fixed ? '2px 0 5px rgba(0,0,0,0.1)' : 'none',
          textAlign,
        } }
        className={ classNames(
          'p-2',
          'border-bottom',
          'border-end',
          rowClassName && (
            typeof rowClassName === 'function' 
              ? rowClassName({ row, rowId: rowIndex })
              : rowClassName
          ),
          { 'cursor-pointer': onRowClick }
        ) }
      >
        { cellContent }
      </td>
    );
  }, [safeColumns, rowClassName, onRowClick]);

  // Компонент строки
  // Компонент строки
  const renderRow = useCallback((row, rowIndex) => {
    if (!row) return null;
    const actualIndex = localPaging && !hidePagination 
      ? currentPage * pageSize + rowIndex
      : rowIndex;
  
    const isHovered = hoveredRowIndex === actualIndex;
  
    return (
      <tr 
        key={ actualIndex }
        className={ classNames(
          { 'row-hover': isHovered },
          { 'clickable-row': onRowClick }
        ) }
        style={ {
          backgroundColor: isHovered ? '#f5f5f5' : 'transparent',
          transition: 'background-color 0.2s ease',
        } }
        onMouseEnter={ () => setHoveredRowIndex(actualIndex) }
        onMouseLeave={ () => setHoveredRowIndex(null) }
        onClick={ () => onRowClick && onRowClick({ row, rowId: actualIndex }) }
      >
        { safeColumns.map((column, colIndex) => renderCell(row, column, actualIndex, colIndex)) }
      </tr>
    );
  }, [
    safeColumns,
    renderCell,
    currentPage,
    pageSize,
    localPaging,
    hidePagination,
    hoveredRowIndex,
    setHoveredRowIndex,
    onRowClick,
  ]);
  // Если нет данных
  if (displayData.length === 0 && !loading) {
    return (
      <div className='text-center p-5 text-muted border rounded'>
        { noDataText }
      </div>
    );
  }

  return (
    <>
      { (loading || isLoadingVisible) && (
        <div 
          className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white z-5'
          style={ {
            opacity: loading ? 1 : 0,
            visibility: loading ? 'visible' : 'hidden',
            transition: 'opacity 200ms ease-in-out, visibility 200ms ease-in-out',
            pointerEvents: loading ? 'auto' : 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.75)', // замена bg-opacity-75 для надёжности
          } }
        >
          <div 
            className='spinner-border text-primary' 
            role='status'
            style={ {
              opacity: loading ? 1 : 0,
              transform: loading ? 'scale(1)' : 'scale(0.95)',
              transition: 'opacity 200ms ease-in-out, transform 200ms ease-in-out',
            } }
          >
            <span className='sr-only'>Загрузка...</span>
          </div>
        </div>
      ) }
      
      <div
        ref={ tableContainerRef }
        className='custom-datatable border rounded'
        style={ {
          display: 'flex',
          flexDirection: 'column',
          height: Math.max(totalTableHeight, minTableHeight),
          maxHeight: Math.min(totalTableHeight, maxTableHeight),
          overflow: 'hidden',
          outline: 'none',
        } }
        tabIndex={ keyboardScrollEnabled ? 0 : undefined }
        onFocus={ (e) => {
          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(13, 110, 253, 0.25)';
        } }
        onBlur={ (e) => {
          e.currentTarget.style.boxShadow = 'none';
        } }
      >
        <div 
          ref={ headerContainerRef }
          style={ {
            overflow: 'hidden',
            flexShrink: 0,
            borderBottom: '2px solid #dee2e6',
            position: 'relative',
            backgroundColor: '#f8f9fa',
            height: headerHeight,
          } }
        >
          <div style={ { minWidth: totalWidth } }>
            <table 
              style={ { 
                width: useColumnWidth ? totalWidth : '100%',
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                borderSpacing: 0,
                margin: 0,
              } }
            >
              <thead>
                <tr>
                  { safeColumns.map((column, index) => renderHeaderCell(column, index)) }
                </tr>
              </thead>
            </table>
          </div>
        </div>
        
        <div 
          ref={ scrollContainerRef }
          style={ {
            flex: 1,
            overflow: 'auto',
            position: 'relative',
            height: tableBodyHeight,
            paddingBottom: '5px',
          } }
        >
          <div style={ { minWidth: totalWidth, width: useColumnWidth ? totalWidth : '100%' } }>
            <table 
              style={ { 
                width: useColumnWidth ? totalWidth : '100%',
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                borderSpacing: 0,
                margin: 0,
              } }
            >
              <tbody>
                { displayData.map((row, index) => renderRow(row, index)) }
                
                { /* Строка сумм - только если есть данные и суммируемые столбцы */ }
                { hasSumRow && displayData.length > 0 && (
                  <tr className='font-weight-bold bg-light'>
                    { safeColumns.map((column, colIndex) => {
                      if (!column || !column.name) return null;
                      
                      // Расчет left для sticky-колонок
                      let left = 0;
                      if (column.fixed) {
                        for (let i = 0;i < colIndex;i++) {
                          if (safeColumns[i]?.fixed) {
                            left += safeColumns[i].width || 200;
                          }
                        }
                      }

                      // Для строки сумм всегда отображаем число (игнорируем getCellValue)
                      const cellContent = column.showSum 
                        ? sumRow[column.name]?.toLocaleString('ru-RU') 
                        : '';
                      
                      const textAlign = column.textAlign || 'right';

                      return (
                        <td
                          key={ `sum-${ column.name }` }
                          style={ {
                            width: column.width || 'auto',
                            minWidth: column.minWidth || column.width || 150,
                            maxWidth: column.maxWidth || 'none',
                            position: column.fixed ? 'sticky' : 'relative',
                            left: column.fixed ? left : `${ left }px`,
                            zIndex: column.fixed ? 26 : 6,
                            backgroundColor: '#f8f9fa',
                            verticalAlign: 'middle',
                            boxShadow: column.fixed ? '2px 0 5px rgba(0,0,0,0.1)' : 'none',
                            textAlign,
                            borderTop: '2px solid #dee2e6',
                          } }
                          className={ classNames(
                            'p-2',
                            'border-bottom-0',
                            'border-end',
                            column.showSum && 'text-nowrap'
                          ) }
                        >
                          { cellContent }
                        </td>
                      );
                    }) }
                  </tr>
                ) }
                
                { /* Добавляем пустую строку для отступа от полосы прокрутки */ }
                { displayData.length > 0 && (
                  <tr style={ { height: scrollbarOffset - 5 } }>
                    <td 
                      colSpan={ safeColumns.length } 
                      style={ { 
                        border: 'none', 
                        height: scrollbarOffset - 5,
                        padding: 0,
                        margin: 0, 
                      } }
                    ></td>
                  </tr>
                ) }
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      { /* Пагинация */ }
      { !hidePagination && dataLength > 0 && (
        <div className='d-flex justify-content-between align-items-center mt-3'>
          <div className='d-flex align-items-center'>
            <span className='mr-2'>Показывать по:</span>
            <Dropdown>
              <Dropdown.Toggle variant='outline-secondary' size='sm'>
                { pageSize }
              </Dropdown.Toggle>
              <Dropdown.Menu>
                { [5, 10, 20, 50, 100].map(size => (
                  <Dropdown.Item 
                    key={ size }
                    active={ size === pageSize }
                    onClick={ () => handlePageSizeChange(size) }
                  >
                    { size }
                  </Dropdown.Item>
                )) }
              </Dropdown.Menu>
            </Dropdown>
            <span className='ml-3'>
              { currentPage * pageSize + 1 }-{ Math.min((currentPage + 1) * pageSize, dataLength) } из { dataLength }
            </span>
          </div>
          
          <div className='d-flex align-items-center'>
            <Button
              variant='outline-secondary'
              size='sm'
              disabled={ currentPage === 0 }
              onClick={ () => handlePageChange(currentPage - 1) }
              className='mr-2'
            >
              <ChevronLeft />
            </Button>
            
            <div className='d-flex'>
              { [...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (currentPage < 2) {
                  pageNum = i;
                } else if (currentPage > totalPages - 3) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={ pageNum }
                    variant={ currentPage === pageNum ? 'primary' : 'outline-secondary' }
                    size='sm'
                    onClick={ () => handlePageChange(pageNum) }
                    className='mx-1'
                    style={ { minWidth: '40px' } }
                  >
                    { pageNum + 1 }
                  </Button>
                );
              }) }
            </div>
            
            <Button
              variant='outline-secondary'
              size='sm'
              disabled={ currentPage >= totalPages - 1 }
              onClick={ () => handlePageChange(currentPage + 1) }
              className='ml-2'
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      ) }
    </>
  );
};

export default CustomDataTable;